import {Pipe, PipeTransform} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';

/**
 * SafeResourceUrlPipe - Validates and sanitizes URLs for iframe src attributes
 *
 * SECURITY NOTE: This pipe uses bypassSecurityTrustResourceUrl() which disables
 * Angular's built-in XSS protection. To mitigate security risks, it implements
 * a whitelist approach that only allows URLs from trusted domains.
 *
 * Only URLs matching the following patterns are allowed:
 * - SAP public domains: *.sap.com
 * - SAP internal domains: *.sap, *.sap.corp, *.sap.internal
 * - SAP store domains: *.store.net.sap
 * - Localhost for development: localhost, 127.0.0.1
 * - Relative URLs (same origin)
 *
 * Any URL not matching the whitelist will be rejected and return null.
 */
@Pipe({
  name: 'safeResourceUrl',
  standalone: true,
})
export class SafeResourceUrlPipe implements PipeTransform {
  // Maximum URL length to prevent DoS
  private readonly MAX_URL_LENGTH = 2048;

  /**
   * Whitelist of allowed URL patterns (regex patterns)
   * All patterns are designed to prevent ReDoS attacks
   * Only URLs matching these patterns will be trusted
   */
  private readonly ALLOWED_URL_PATTERNS: RegExp[] = [
    // Pattern #1: SAP public domains (*.sap.com and sap.com)
    // Regex breakdown:
    //   ^https?:\/\/              - Start with http:// or https://
    //   (?:\w[\w-]*\.)*           - Optional subdomains: word char start, then word chars/hyphens, followed by dot (repeating)
    //   sap\.com                  - Must end with sap.com (literal)
    //   (?::\d+)?                 - Optional port number
    //   (?:[/?#].*)?$             - Optional path, query string, or fragment
    // Examples: https://sap.com, https://www.sap.com, https://help.sap.com, https://api.sap.com:443/path
    // Blocks: https://.sap.com, https://...sap.com, https://notsap.com, https://sap.com.evil.com
    /^https?:\/\/(?:\w[\w-]*\.)*sap\.com(?::\d+)?(?:[/?#].*)?$/i,

    // Pattern #2: SAP internal domains (*.sap)
    // Regex breakdown:
    //   ^https?:\/\/        - Start with http:// or https://
    //   \w[\w-]*            - First domain label: starts with word char, then word chars or hyphens
    //   (?:\.\w[\w-]*)*     - Additional domain labels (repeating, non-capturing)
    //   \.sap               - Must end with .sap TLD
    //   (?::\d+)?           - Optional port number
    //   (?:[/?#].*)?$       - Optional path, query string, or fragment
    // Examples: https://subdomain.sap, https://my-app.sap:8080/path
    // Blocks: https://.sap, https://...sap, https://-invalid.sap
    /^https?:\/\/\w[\w-]*(?:\.\w[\w-]*)*\.sap(?::\d+)?(?:[/?#].*)?$/i,

    // Pattern #3: SAP corporate domains (*.sap.corp, *.sap.internal)
    // Regex breakdown:
    //   ^https?:\/\/        - Start with http:// or https://
    //   \w[\w-]*            - First domain label
    //   (?:\.\w[\w-]*)*     - Additional domain labels
    //   \.sap\.             - Must have .sap. in domain
    //   (?:corp|internal)   - Explicitly allow only .corp or .internal TLDs (security whitelist)
    //   (?::\d+)?           - Optional port number
    //   (?:[/?#].*)?$       - Optional path, query, or fragment
    // Examples: https://internal.sap.corp, https://app.sap.internal:9000
    // Blocks: https://test.sap.net, https://test.sap.org (other TLDs not in whitelist)
    /^https?:\/\/\w[\w-]*(?:\.\w[\w-]*)*\.sap\.(?:corp|internal)(?::\d+)?(?:[/?#].*)?$/i,

    // Pattern #4: SAP store domains (*.store.net.sap)
    // Regex breakdown:
    //   ^https?:\/\/        - Start with http:// or https://
    //   \w[\w-]*            - First domain label
    //   (?:\.\w[\w-]*)*     - Additional domain labels
    //   \.store\.net\.sap   - Must end with .store.net.sap
    //   (?::\d+)?           - Optional port number
    //   (?:[/?#].*)?$       - Optional path, query, or fragment
    // Examples: https://dcp-support-ui.d1.services.store.net.sap/iframe
    // Includes both *.services.store.net.sap and *.storefront.store.net.sap
    /^https?:\/\/\w[\w-]*(?:\.\w[\w-]*)*\.store\.net\.sap(?::\d+)?(?:[/?#].*)?$/i,

    // Pattern #5: Localhost for development (IPv4, IPv6)
    // Regex breakdown:
    //   ^https?:\/\/                    - Start with http:// or https://
    //   (?:localhost|127\.0\.0\.1|\[::1\])  - localhost, 127.0.0.1, or [::1] (IPv6)
    //   (?::\d+)?                       - Optional port number
    //   (?:[/?#].*)?$                   - Optional path, query, or fragment
    // Examples: http://localhost:4200, http://127.0.0.1:8080, http://[::1]:3000
    /^https?:\/\/(?:localhost|127\.0\.0\.1|\[::1\])(?::\d+)?(?:[/?#].*)?$/i,

    // Pattern #6: Relative URLs (same origin - most secure)
    // Regex breakdown:
    //   ^\/           - Must start with single forward slash
    //   (?:[^/].*)?   - Optionally followed by non-slash char and anything else
    //   $             - End of string
    // Examples: /, /path, /path/to/resource, /api?query=value#anchor
    // Blocks: //evil.com (protocol-relative URLs to external domains)
    /^\/(?:[^/].*)?$/,
  ];

  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Validates and sanitizes a URL for use in iframe src attribute
   *
   * @param url - The URL to validate and sanitize
   * @returns SafeResourceUrl if the URL is trusted, null otherwise
   */
  transform(url: string): SafeResourceUrl | null {
    if (!url) {
      return null;
    }

    // Prevent extremely long URLs (potential DoS)
    if (url.length > this.MAX_URL_LENGTH) {
      console.error(
        '[SafeResourceUrlPipe] Security: URL exceeds maximum length',
        `(${this.MAX_URL_LENGTH} chars).`,
        'URL length:',
        url.length,
      );
      return null;
    }

    // Validate the URL against whitelist
    if (!this.isUrlAllowed(url)) {
      console.error(
        '[SafeResourceUrlPipe] Security: Blocked untrusted URL from iframe src.',
        'URL must match allowed patterns (SAP domains, localhost, or relative URLs).',
        'Blocked URL:',
        url,
      );
      return null;
    }

    // URL is trusted, bypass security, disable sonar warning
    return this.sanitizer.bypassSecurityTrustResourceUrl(url); // NOSONAR
  }

  /**
   * Checks if a URL matches any of the allowed patterns
   *
   * @param url - The URL to validate
   * @returns true if URL matches whitelist, false otherwise
   */
  private isUrlAllowed(url: string): boolean {
    // Trim whitespace
    const trimmedUrl = url.trim();

    // Check against each allowed pattern
    for (const pattern of this.ALLOWED_URL_PATTERNS) {
      if (pattern.test(trimmedUrl)) {
        return true;
      }
    }

    return false;
  }
}
